import { ReactElement, useEffect, useState } from "react";

import dayjs from "dayjs";
import styled from "styled-components";

import { PriceLabel } from "components/PriceLabel/PriceLabel";
import { Switch } from "components/Switch/Switch";
import { showCommentInHTML } from "helpers/comment";
import { isOverCutOffDate } from "helpers/date";
import { RESPONSIVE } from "helpers/responsive";
import { MovementLogApprovalListItem } from "models/movementLog";
import * as Layout from "molecules/Layout";
import * as Table from "molecules/Table";

const ALLOW_DENY_STATUS = ["applying", "approved"];

const TravelExpenseApplicationsTableActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const TravelExpenseApplicationsTableThActions = styled(Table.Th)`
  width: 110px;

  @media ${RESPONSIVE.DESKTOP} {
    width: 170px;
  }
`;

const TravelExpenseApplicationsTableThComments = styled(Table.Th)`
  @media ${RESPONSIVE.MOBILE} {
    display: none;
  }
`;

const TravelExpenseApplicationsTableTdComments = styled(Table.Td)`
  @media ${RESPONSIVE.MOBILE} {
    display: none;
  }
`;

const renderMovementLogApprovals = (
  movementLogApproval: MovementLogApprovalListItem,
  _toggleBulkApprovalDialog: (
    applyingMovementLogApprovals: MovementLogApprovalListItem[]
  ) => void,
  toggleTravelExpenseApprovalDialogToApprove: (
    selectedMovementLogApproval: MovementLogApprovalListItem
  ) => void,
  toggleTravelExpenseApprovalDialogToDeny: (
    selectedMovementLogApproval: MovementLogApprovalListItem
  ) => void
) => {
  const { user } = movementLogApproval;
  if (!user) return <tr key={movementLogApproval.id} />;

  return (
    <tr key={movementLogApproval.id}>
      <Table.Td>
        <a
          href={`approver/users/${user.id}/movement_logs/${movementLogApproval.id}`}
        >
          {movementLogApproval.id}
        </a>
      </Table.Td>
      <Table.Td>
        <span>{user.nameJp}</span>
        <br />
        <span>({user.employeeId})</span>
      </Table.Td>
      <Table.Td>{movementLogApproval.approvalStatusName}</Table.Td>
      <TravelExpenseApplicationsTableTdComments>
        {showCommentInHTML(movementLogApproval.approverComment)}
      </TravelExpenseApplicationsTableTdComments>
      <TravelExpenseApplicationsTableTdComments>
        {showCommentInHTML(movementLogApproval.applicantComment)}
      </TravelExpenseApplicationsTableTdComments>
      <Table.Td>
        <PriceLabel
          amount={movementLogApproval.summary.commutingExpenses.totalFare}
        />
      </Table.Td>
      <Table.Td>
        <PriceLabel
          amount={movementLogApproval.summary.travelExpenses.totalFare}
        />
      </Table.Td>
      <Table.Td>
        {!isOverCutOffDate(dayjs(movementLogApproval.approvalMonth)) && (
          <div className="buttons has-addons">
            {ALLOW_DENY_STATUS.includes(movementLogApproval.approvalStatus) && (
              <button
                type="button"
                className="button"
                onClick={(_event) =>
                  toggleTravelExpenseApprovalDialogToDeny(movementLogApproval)
                }
              >
                否認
              </button>
            )}
            {movementLogApproval.approvalStatus === "applying" && (
              <button
                type="button"
                className="button is-primary"
                onClick={(_event) =>
                  toggleTravelExpenseApprovalDialogToApprove(
                    movementLogApproval
                  )
                }
              >
                承認
              </button>
            )}
          </div>
        )}
      </Table.Td>
    </tr>
  );
};

export interface TravelExpenseApplicationsTableProps {
  movementLogApprovals: MovementLogApprovalListItem[];
  toggleBulkApprovalDialog: (
    applyingMovementLogApprovals: MovementLogApprovalListItem[]
  ) => void;
  toggleTravelExpenseApprovalDialogToApprove: (
    selectedMovementLogApproval: MovementLogApprovalListItem
  ) => void;
  toggleTravelExpenseApprovalDialogToDeny: (
    selectedMovementLogApproval: MovementLogApprovalListItem
  ) => void;
}

export function TravelExpenseApplicationsTable({
  movementLogApprovals,
  toggleBulkApprovalDialog,
  toggleTravelExpenseApprovalDialogToApprove,
  toggleTravelExpenseApprovalDialogToDeny,
}: TravelExpenseApplicationsTableProps): ReactElement | null {
  const [employeeId, setEmployeeId] = useState("");
  const [filtered, setFiltered] = useState([] as MovementLogApprovalListItem[]);
  const [isShowApproved, setShowApproved] = useState(false);

  // NOTE
  // approvalsの対象月をDateとして渡す予定だったけど、Applicationsでgroup化する際に文字列化していて
  // うまくParseされなかったので影響範囲を考慮して一旦先頭のデータをチェックする形にした
  const isApprovalsOverCutOffDate = isOverCutOffDate(
    dayjs(movementLogApprovals[0].approvalMonth)
  );

  const filterApprovalStatus = (
    movementLogApprovals_: MovementLogApprovalListItem[],
    status: string[]
  ) =>
    movementLogApprovals_.filter(
      (approval) =>
        status.filter((state) => approval.approvalStatus === state).length > 0
    );

  const filterEmployeeId = (
    movementLogApprovals_: MovementLogApprovalListItem[],
    employeeId_: string
  ) =>
    movementLogApprovals_.filter((approval) =>
      approval.user ? approval.user.employeeId.indexOf(employeeId_) >= 0 : false
    );

  const inputEmployeeId = (employeeId_: string) => {
    setEmployeeId(employeeId_);

    let data = movementLogApprovals;
    if (!isShowApproved) {
      data = filterApprovalStatus(data, ["unapplied", "applying"]);
    }

    setFiltered(filterEmployeeId(data, employeeId_));
  };

  const switchShowApproved = () => {
    setShowApproved(!isShowApproved);

    let data = movementLogApprovals;
    if (employeeId) {
      data = filterEmployeeId(data, employeeId);
    }
    if (isShowApproved) {
      data = filterApprovalStatus(data, ["unapplied", "applying"]);
    }
    setFiltered(data);
  };

  const hasApplying = (movementLogApprovals_: MovementLogApprovalListItem[]) =>
    filterApprovalStatus(movementLogApprovals_, ["applying"]).length > 0;

  useEffect(() => {
    if (movementLogApprovals) {
      setFiltered(
        filterApprovalStatus(movementLogApprovals, ["unapplied", "applying"])
      );
    }
    setShowApproved(false);
    setEmployeeId("");
  }, [movementLogApprovals]);

  return (
    <>
      <Layout.FlexWrapper alignItems="flex-start">
        <div />
        <Switch
          id="showMovementLogApproved"
          name="showMovementLogApproved"
          value={isShowApproved}
          rtl
          onChange={switchShowApproved}
        >
          承認済みを表示
        </Switch>
      </Layout.FlexWrapper>
      <Table.Table>
        <thead>
          <tr>
            <Table.ThSmall>申請ID</Table.ThSmall>
            <Table.ThMedium>
              申請者 <br />
              <input
                className="input"
                type="text"
                value={employeeId}
                placeholder="社員番号"
                onChange={(event) => inputEmployeeId(event.target.value)}
              />
            </Table.ThMedium>
            <Table.ThSmall>申請ステータス</Table.ThSmall>
            <TravelExpenseApplicationsTableThComments>
              あなたのコメント
            </TravelExpenseApplicationsTableThComments>
            <TravelExpenseApplicationsTableThComments>
              申請者のコメント
            </TravelExpenseApplicationsTableThComments>
            <Table.ThMedium>通勤費</Table.ThMedium>
            <Table.ThSmall>交通費</Table.ThSmall>
            <TravelExpenseApplicationsTableThActions>
              アクション
            </TravelExpenseApplicationsTableThActions>
          </tr>
        </thead>
        <tbody>
          {filtered.map((movementLogApproval) =>
            renderMovementLogApprovals(
              movementLogApproval,
              toggleBulkApprovalDialog,
              toggleTravelExpenseApprovalDialogToApprove,
              toggleTravelExpenseApprovalDialogToDeny
            )
          )}
        </tbody>
      </Table.Table>
      <TravelExpenseApplicationsTableActions>
        {!isApprovalsOverCutOffDate && (
          <button
            type="button"
            disabled={!hasApplying(movementLogApprovals)}
            className="button is-primary"
            onClick={(_event) => toggleBulkApprovalDialog(filtered)}
          >
            一括承認
          </button>
        )}
      </TravelExpenseApplicationsTableActions>
    </>
  );
}
